import { createFileRoute } from '@tanstack/react-router';

import { LineChartUp } from '@mint-lib/icons';
import {
  buildGuards,
  hasFlagEnabled,
  hasPermission,
  isAgencyCompany,
  not,
} from '@mint-lib/routing-guards';
import {
  fetchCompanyInfoMint2QueryOptions,
  fetchCompanyInfoQueryOptions,
  fetchDimensionsListQueryOptions,
  fetchPivotCurrenciesMint2QueryOptions,
  fetchPivotCurrenciesQueryOptions,
  fetchPivotDimensionsMint2QueryOptions,
  fetchPivotDimensionsQueryOptions,
  fetchPivotMetricsMint2QueryOptions,
  fetchPivotMetricsQueryOptions,
  fetchUserCurrencyQueryOptions,
  fetchWorkspaceInfoQueryOptions,
} from '@mint-sdk/api-dashboards/compat';

const reportingMint2Guard = buildGuards(hasFlagEnabled('reportingMint2'));
const reportingMint2NextGuard = buildGuards(
  hasFlagEnabled('reportingMint2'),
  hasFlagEnabled('reportingMint2Next'),
);

// TODO(STZF-23): fix guard duplication with sidebar in permissions refactoring
const sidebarGuard = buildGuards(
  not(isAgencyCompany),
  hasPermission('campaignInsightsFeature:view'),
);

export const Route = createFileRoute('/_secured/reporting')({
  staticData: {
    commandMeta: ({ t, check }) => {
      if (!check(sidebarGuard)) {
        return;
      }

      return {
        title: t('Insights'),
        keywords: [t('reporting'), t('dashboards'), t('analytics')],
        icon: LineChartUp,
      };
    },
  },
  loader: async ({ context }) => {
    const i18n = await context.getInstance('i18n', '@mint/routing');
    const title = i18n.t('Reporting | MINT');

    const [flagsmith, queryClient, profile] = await Promise.all([
      context.getInstance('flagsmith', '@mint/arm'),
      context.serviceLocator.getOrThrowInstance('queryClient'),
      context.serviceLocator.getOrThrowInstance('profile'),
      // Preload translations for all the necessary namespaces to ensure they're ready when the component renders
      context.getInstance('i18n', '@myn/dashboards-builder'),
      context.getInstance('i18n', '@mint-lib/insights-shared'),
      context.getInstance('i18n', '@mint-lib/pivot-table'),
      context.getInstance('i18n', '@mint-lib/manage-permission'),
      context.getInstance('i18n', '@myn/pivot'),
    ]);
    const guardContext = await context.getGuardContext(flagsmith);
    const isMINTv2 = reportingMint2Guard(guardContext);
    const isMINTv2Next = reportingMint2NextGuard(guardContext);

    const companyId = profile
      .getProfileData()
      ?.profile?.company?.id?.toString();

    if (companyId) {
      if (isMINTv2) {
        void Promise.allSettled([
          isMINTv2Next
            ? queryClient.prefetchQuery(fetchDimensionsListQueryOptions({}))
            : queryClient.prefetchQuery(
                fetchPivotDimensionsMint2QueryOptions({
                  urlParams: { companyId },
                }),
              ),
          queryClient.prefetchQuery(
            fetchPivotMetricsMint2QueryOptions({
              urlParams: { companyId },
            }),
          ),
          isMINTv2Next
            ? queryClient.prefetchQuery(fetchWorkspaceInfoQueryOptions({}))
            : queryClient.prefetchQuery(
                fetchCompanyInfoMint2QueryOptions({
                  urlParams: { companyId },
                }),
              ),
          queryClient.prefetchQuery(
            fetchPivotCurrenciesMint2QueryOptions({
              urlParams: { companyId },
            }),
          ),
          queryClient.prefetchQuery(fetchUserCurrencyQueryOptions({})),
          // TODO: Add prefetch queries: Dashboards and DashboardsListSettings
        ]);
      } else {
        void Promise.allSettled([
          queryClient.prefetchQuery(
            fetchPivotDimensionsQueryOptions({
              urlParams: { companyId },
            }),
          ),
          queryClient.prefetchQuery(
            fetchPivotMetricsQueryOptions({
              urlParams: { companyId },
            }),
          ),
          queryClient.prefetchQuery(
            fetchCompanyInfoQueryOptions({
              urlParams: { companyId },
            }),
          ),
          queryClient.prefetchQuery(
            fetchPivotCurrenciesQueryOptions({
              urlParams: { companyId },
            }),
          ),
          queryClient.prefetchQuery(fetchUserCurrencyQueryOptions({})),
        ]);
      }
    }

    return { title };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.title;
    return {
      meta: [
        {
          title,
        },
      ],
    };
  },
});
